/* eslint-disable react/jsx-no-useless-fragment */
import React, { Fragment, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { programsApi } from 'api';
import { useGlobal } from 'contexts/globalContext';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { dateFormat } from 'utils/dateFormat';
import { classNames } from 'primereact/utils';
import { DialogAction, DialogConfirm } from 'components/_new/Dialog';
import { Button } from 'components/_new/Button';
import { InputText } from 'components/_new/InputText';
import { Controller, useForm } from 'react-hook-form';
import { Dropdown } from 'components/_new/Dropdown';
import { Paginator, PaginatorPageState } from 'primereact/paginator';
import { Loader } from 'components/_new/Loader';
import download from 'downloadjs';
import { Link } from 'components/_new/Link';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Box, BoxSection } from 'components/_new/Box';
import { useUrlSearch } from 'utils/useUrlSearch';
import { ProgramRole } from 'types/Membership';
import { Field } from 'components/_new/Field';
import { MultiSelect } from 'components/_new/MultiSelect';
import { userDisplayName } from 'utils/userDisplayName';
import { Pair } from 'types/Pair';
import { AvatarSet } from 'components/_new/AvatarSet';
import { Checkbox } from 'primereact/checkbox';
import dayjs from 'dayjs';
import { User } from '../../types/User';
import { ApplicationRole } from '../../types/Application';
import { EditApplicationDialog } from './EditApplicationDialog';
import { AddUserDialog } from '../_common/panel/AddUserDialog';
import { EmptyState } from '../../utils/emptyState';

type PairOption = {
	users: User[];
	label: string;
	names: string[];
	value: number;
};

type MembershipFilter = {
	search: string;
	role?: ProgramRole;
	application?: 'with-application' | 'without-application';
};

const ProgramMembershipList = () => {
	const { toastRef } = useGlobal();
	const { panelPath } = useProgramPanel();

	const { set, setMany, params } = useUrlSearch<{
		search?: string;
		role?: ProgramRole;
		application?: 'with-application' | 'without-application';
		page: number;
	}>({ page: 1 });

	const { search, role, application, page } = params;
	const queryFilter = { search, role, application };

	const { control, handleSubmit } = useForm<MembershipFilter>({
		defaultValues: {
			search: params.search,
			role: params.role,
			application: params.application,
		},
	});

	const [addUserModal, setAddUserModal] = useState(false);
	const [deleteAllWithoutApplicationDialogOpen, setDeleteAllWithoutApplicationDialogOpen] = useState(false);
	const [chooseRoleModalOpen, setChooseRoleModalOpen] = useState(false);
	const { t } = useTranslation();

	const { data: program, isLoading, isSuccess } = useProgramPanel();

	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [editApplicationDialogOpen, setEditApplicationDialogOpen] = useState<boolean>(false);
	const [editApplicationApplicationRole, setEditApplicationApplicationRole] = useState<ApplicationRole>('mentee');
	const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
	const [deleteMembershipDialogOpen, setDeleteMembershipDialogOpen] = useState(false);
	const [deletePersonalDialogOpen, setDeletePersonalDataDialogOpen] = useState(false);

	const {
		mutate: manageMembershipMutate,
		isLoading: manageMembershipLoading,
		variables: manageMembershipVariables,
	} = useMutation(['manageMyProgramMembership'], programsApi.manageMyProgramMembership, {
		onSuccess: () => {
			refetchMemberships();
			toastRef?.current?.show({
				severity: 'success',
				life: 3000,
				summary: t('misc.success'),
				detail: t('misc.updatedSuccessfully'),
			});
		},
	});

	const { mutate: removeMembershipMutate, isLoading: removeMembershipLoading } = useMutation(
		['removeMyProgramMembership'],
		programsApi.removeMyProgramMembership,
		{
			onSuccess: () => {
				refetchMemberships();
				setDeleteMembershipDialogOpen(false);
				setSelectedMembershipId(null);
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('misc.deletedSuccessfully'),
				});
			},
		}
	);

	const { mutate: removeAllWithoutApplicationMutate, isLoading: removeAllWithoutApplicationLoading } = useMutation(
		['removeAllWithoutApplicationMutate'],
		programsApi.removeAllWithoutApplicationMutate,
		{
			onSuccess: () => {
				refetchMemberships();
				setDeleteAllWithoutApplicationDialogOpen(false);
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('misc.deletedSuccessfully'),
				});
			},
		}
	);

	const handlePageChange = ({ page }: PaginatorPageState) => {
		set('page', String(page + 1));
	};

	const handleMembershipDelete = (membershipId: number) => {
		removeMembershipMutate({ id: Number(program?.id), membershipId });
	};

	const [quickDelete, setQuickDelete] = useState(false);
	const { mutate: removePersonalDataMutate, isLoading: removePersonalDataLoading } = useMutation(
		['removePersonalData'],
		programsApi.removePersonalData,
		{
			onSuccess: () => {
				refetchMemberships();
				setDeletePersonalDataDialogOpen(false);
				setQuickDelete(false);
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('misc.deletedSuccessfully'),
				});
			},
		}
	);
	const handlePersonalDataDeleteMutate = (membershipId: number) => {
		removePersonalDataMutate({ programId: Number(program?.id), membershipId, quickDelete });
	};

	const {
		data: programMemberships,
		isSuccess: getProgramMembershipsSuccess,
		isLoading: getProgramMembershipLoading,
		isFetching: getProgramMembershipFetching,
		refetch: refetchMemberships,
	} = useQuery(
		['getProgramMemberships', { id: program?.id, page, filter: queryFilter }],
		() => programsApi.getProgramAdminMemberships({ id: Number(program?.id), page, filter: queryFilter }),
		{
			enabled: Boolean(program?.id && isSuccess),
		}
	);

	const [downoadXlsLoading, setDownoadXlsLoading] = useState(false);

	const handleSubmitForm = handleSubmit(({ search, role, application }) => {
		const prepared = {
			page: undefined,
			search: search && String(search).trim().length >= 3 ? String(search).trim() : undefined,
			role,
			application,
		};
		setMany(prepared);
	});

	const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);
	const selectedMembership = programMemberships?.data.find(({ id }) => id === selectedMembershipId) || null;

	const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
	const {
		watch: roleFormWatch,
		control: roleFormControl,
		handleSubmit: roleFormHandleSubmit,
		reset: roleFormReset,
	} = useForm<{ role: ProgramRole; pairs: number[] }>({
		defaultValues: {
			role: ProgramRole.USER,
			pairs: [],
		},
	});
	const roleFormHandleSubmitForm = roleFormHandleSubmit(({ role, pairs }) => {
		if (selectedMembershipId) {
			manageMembershipMutate({ id: Number(program?.id), membershipId: selectedMembershipId, role, pairs });
		}
	});
	const isCoordinatorRole = roleFormWatch('role') === ProgramRole.PAIRS_COORDINATOR;

	const {
		data: availableCoordinatePairs,
		isLoading: availableCoodinatePairsLoading,
		refetch: availableCoodinatePairsRefetch,
	} = useQuery(
		['PROGRAM_PANEL_MEMBERSHIPS_PARIS_FOR_COORDINATOR', { programId: Number(program?.id) }],
		() => programsApi.getAvailableCoordinatePairsCoordinator({ programId: Number(program?.id) }),
		{ enabled: isCoordinatorRole }
	);

	const mapPairOption = ({
		id,
		menteeMembership: { user: menteeUser },
		mentorMembership: { user: mentorUser },
	}: Pair): PairOption => ({
		users: [menteeUser, mentorUser],
		label: `${userDisplayName(menteeUser)} z ${userDisplayName(mentorUser)}`,
		names: [userDisplayName(menteeUser), userDisplayName(mentorUser)],
		value: id,
	});
	const pairOptionTemplate = ({ users, label, names }: PairOption) => (
		<div className="inline-flex flex-row gap-2 align-items-center mr-2">
			<AvatarSet
				avatars={users.map((user) => ({
					name: userDisplayName(user),
					src: user.avatarActive ? user.avatar : undefined,
				}))}
			/>
			<span>{label}</span>
		</div>
	);
	const pairSelectedOptionTemplate = (value: number) => {
		const pair = (availableCoordinatePairs || []).find(({ id }) => id === value);
		if (!pair) {
			return null;
		}
		const option = mapPairOption(pair);
		return pairOptionTemplate(option);
	};

	useEffect(() => {
		if (changeRoleDialogOpen && isCoordinatorRole) {
			availableCoodinatePairsRefetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [changeRoleDialogOpen, availableCoodinatePairsRefetch]);

	const availableCoordinatePairsMapped = [
		{
			label: 'test',
			items: (availableCoordinatePairs || [])
				.sort((a, b) =>
					String(a.menteeMembership.user.lastName || '').localeCompare(
						String(b.mentorMembership.user.lastName || ''),
						'en',
						{ sensitivity: 'base' }
					)
				)
				.map(mapPairOption),
		},
	];

	return (
		<>
			<EditApplicationDialog
				visible={!!editApplicationDialogOpen}
				onHide={() => setEditApplicationDialogOpen(false)}
				user={selectedUser}
				programId={Number(program?.id)}
				programMembershipId={selectedMembershipId}
				applicationRole={editApplicationApplicationRole}
				editMode={false}
				onApplicationAdded={() => {
					refetchMemberships();
				}}
				onCancel={() => {
					setSelectedMembershipId(null);
					setSelectedUser(null);
				}}
				addNewMode
			/>
			<DialogAction
				title={t('programPanel.memberships.addMentorOrMentee')}
				visible={chooseRoleModalOpen}
				onHide={() => {
					setChooseRoleModalOpen(false);
				}}
				actions={[
					{
						key: 'mentee',
						label: 'mentee',
						onClick: () => {
							setChooseRoleModalOpen(false);
							setEditApplicationDialogOpen(true);
							setEditApplicationApplicationRole('mentee');
						},
					},
					{
						key: 'mentor',
						label: 'mentor',
						onClick: () => {
							setChooseRoleModalOpen(false);
							setEditApplicationDialogOpen(true);
							setEditApplicationApplicationRole('mentor');
						},
					},
				]}
			/>
			<DialogAction
				title={t('programPanel.memberships.changeProgramRoleTitle')}
				visible={changeRoleDialogOpen && Boolean(selectedMembershipId)}
				onHide={() => {
					setChangeRoleDialogOpen(false);
					setSelectedMembershipId(null);
					roleFormReset({
						role: ProgramRole.USER,
						pairs: [],
					});
				}}
				actions={[
					{
						key: 'save',
						label: t('actions.save'),
						form: 'change-role-form',
						submit: true,
					},
				]}
			>
				<form id="change-role-form" onSubmit={roleFormHandleSubmitForm} className="flex flex-column gap-2">
					<Controller
						control={roleFormControl}
						name="role"
						rules={{ required: t('programPanel.selectedPersons.required') }}
						render={({ field }) => (
							<Field label={t('programPanel.changeRoleDialog.role')}>
								<Dropdown
									{...field}
									options={[
										{
											label: t('programPanel.memberships.programRole.user'),
											value: ProgramRole.USER,
										},
										{
											label: t('programPanel.memberships.programRole.admin'),
											value: ProgramRole.PROGRAM_ADMIN,
										},
										{
											label: t('programPanel.memberships.programRole.coordinator'),
											value: ProgramRole.PAIRS_COORDINATOR,
										},
									]}
								/>
							</Field>
						)}
					/>

					{isCoordinatorRole && (
						<Controller
							control={roleFormControl}
							name="pairs"
							rules={{
								required: {
									value: isCoordinatorRole,
									message: t('programPanel.selectedPersons.required'),
								},
							}}
							render={({ field: { ref, ...field }, fieldState: { error } }) => (
								<Field label={t('programPanel.changeRoleDialog.coordinatedPairs')}>
									<MultiSelect
										{...field}
										options={availableCoordinatePairsMapped}
										className="p-multiselect-longlist w-full"
										itemTemplate={pairOptionTemplate}
										placeholder={t('misc.dropdownPleaseSelect')}
										selectedItemTemplate={pairSelectedOptionTemplate}
										panelClassName="px-3"
										loading={availableCoodinatePairsLoading}
										filter
										filterMatchMode="contains"
									/>
									{error && <small className="p-error font-bold ml-3">{error.message}</small>}
								</Field>
							)}
						/>
					)}
				</form>
			</DialogAction>
			<DialogConfirm
				title={t('programPanel.memberships.confirmDelete')}
				visible={deleteMembershipDialogOpen}
				onHide={() => {
					setDeleteMembershipDialogOpen(false);
					setSelectedMembershipId(null);
				}}
				onConfirm={() => {
					if (selectedMembershipId) {
						handleMembershipDelete(selectedMembershipId);
					}
				}}
				message={t('programPanel.memberships.deleteWarning')}
			/>
			<DialogConfirm
				title={t('programPanel.memberships.confirmDeleteUser')}
				visible={deletePersonalDialogOpen}
				onHide={() => {
					setDeletePersonalDataDialogOpen(false);
					setSelectedMembershipId(null);
				}}
				onConfirm={() => {
					if (selectedMembershipId) {
						handlePersonalDataDeleteMutate(selectedMembershipId);
					}
				}}
			>
				{selectedMembership?.user.deletedAt ? (
					<p>{t('programPanel.memberships.deleteUserCancel')}</p>
				) : (
					<>
						<p>
							{selectedMembership?.user.deletedAt
								? t('programPanel.memberships.deleteUserCancel')
								: t('programPanel.memberships.deleteUserWarning')}
						</p>
						<div>
							<label>
								<Checkbox
									checked={quickDelete}
									onChange={({ checked }) => {
										setQuickDelete(checked);
									}}
								/>
								<span className="ml-2">{t('programPanel.memberships.quickDelete')}</span>
							</label>
						</div>
					</>
				)}
			</DialogConfirm>
			<DialogConfirm
				title={t('programPanel.memberships.removingInactiveUsers')}
				visible={deleteAllWithoutApplicationDialogOpen}
				onHide={() => {
					setDeleteAllWithoutApplicationDialogOpen(false);
				}}
				onConfirm={() => {
					removeAllWithoutApplicationMutate({ programId: Number(program?.id) });
				}}
				message={t('programPanel.memberships.removeAllWithoutApplicationWarning')}
				confirmAction={{ loading: removeAllWithoutApplicationLoading }}
			/>
			<AddUserDialog
				visible={editUserDialogOpen || addUserModal}
				onHide={() => {
					setEditUserDialogOpen(false);
					setAddUserModal(false);
					setSelectedUser(null);
				}}
				isOrganisationPanel={false}
				program={program}
				editMode={editUserDialogOpen}
				userToEdit={selectedUser}
				onUserAddSuccess={() => {
					refetchMemberships();
				}}
			/>
			<Helmet title={t('organizationPanel.organizationMemberships')} />
			<SubPageTitle
				title={t('organizationPanel.organizationMemberships')}
				additional={program?.displayName}
				right={
					<div className="flex flex-row gap-1">
						<Button
							label={t('misc.downloadXls')}
							loading={downoadXlsLoading}
							onClick={() => {
								setDownoadXlsLoading(true);
								programsApi
									.downloadProgramAdminMembershipsXls({ programId: Number(program?.id) })
									.then((responseData: Blob) => {
										download(
											responseData,
											'memberships.xlsx',
											'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
										);
										setTimeout(() => {
											setDownoadXlsLoading(false);
										}, 3000);
									});
							}}
						/>
						<Button label={t('userPanel.myAccount.addNewUser')} onClick={() => setAddUserModal(true)} />
						<Button
							label={t('userPanel.myAccount.deleteUserWithoutApplication')}
							variant="primary-outlined"
							onClick={() => setDeleteAllWithoutApplicationDialogOpen(true)}
						/>
					</div>
				}
			/>
			<div className="text-sm px-1 mb-3 mx-1">
				<p>{t('programPanel.memberships.desc')}</p>
			</div>
			<div className="flex flex-column gap-2">
				<div className="flex flex-column gap-2">
					<form onSubmit={handleSubmitForm} className="flex flex-row gap-2 flex-wrap justify-content-between">
						<div className="flex flex-row gap-2 flex-wrap">
							<Controller
								control={control}
								name="search"
								render={({ field }) => (
									<InputText
										type="search"
										maxLength={50}
										placeholder={t('programPanel.application.searchFilterOptionPlaceholder')}
										inline
										disabled={getProgramMembershipFetching}
										{...field}
									/>
								)}
							/>
							<Controller
								control={control}
								name="role"
								render={({ field }) => (
									<Dropdown
										{...field}
										inline
										withEmpty
										emptyLabel={t('programPanel.memberships.roleFilterOptions.all')}
										placeholder={t('programPanel.memberships.roleFilterOptions.all')}
										options={[
											{
												label: t('programPanel.memberships.roleFilterOptions.admins'),
												value: ProgramRole.PROGRAM_ADMIN,
											},
											{
												label: t('programPanel.memberships.roleFilterOptions.pairCoordinators'),
												value: ProgramRole.PAIRS_COORDINATOR,
											},
											{
												label: t('programPanel.memberships.roleFilterOptions.users'),
												value: ProgramRole.USER,
											},
										]}
										disabled={getProgramMembershipFetching}
									/>
								)}
							/>
							<Controller
								control={control}
								name="application"
								render={({ field }) => (
									<Dropdown
										{...field}
										inline
										withEmpty
										emptyLabel={t('programPanel.memberships.withApplicationsFilterOptions.all')}
										placeholder={t('programPanel.memberships.withApplicationsFilterOptions.all')}
										options={[
											{
												label: t(
													'programPanel.memberships.withApplicationsFilterOptions.onlyWithApplications'
												),
												value: 'with-application',
											},
											{
												label: t(
													'programPanel.memberships.withApplicationsFilterOptions.onlyWithoutApplications'
												),
												value: 'without-application',
											},
										]}
										disabled={getProgramMembershipFetching}
									/>
								)}
							/>
						</div>
						<Button submit label={t('programPanel.memberships.filter')} loading={isLoading} />
					</form>
				</div>

				{(isLoading || getProgramMembershipLoading) && <Loader />}

				{!getProgramMembershipLoading && (
					<div className="text-sm px-1 mb-3 mx-1">
						<Trans
							i18nKey="programPanel.memberships.summary"
							components={{ b: <b /> }}
							values={{
								count: programMemberships?.pagination.all,
								currentPage: programMemberships?.pagination.currentPage,
								pages: programMemberships?.pagination.pages,
							}}
						/>
					</div>
				)}

				{isSuccess && getProgramMembershipsSuccess && (
					<div className="flex flex-column gap-2">
						{getProgramMembershipsSuccess && programMemberships.data.length === 0 && (
							<EmptyState title={t('programPanel.memberships.noMemberships')} />
						)}
						{getProgramMembershipsSuccess && programMemberships.data.length > 0 && (
							<ul className="p-list-unstylled flex flex-column gap-2">
								{programMemberships.data.map((membership) => {
									const twoFactorAuthEnabled =
										membership.user.twoFactorAuth ||
										Number(membership.user.stats?.organizationAdminCount) > 0 ||
										Number(membership.user.stats?.programAdminCount) > 0 ||
										Number(membership.user.stats?.program2faReqCount) > 0;
									return (
										<Fragment key={membership.id}>
											<li>
												<Tooltip
													target={`#two-factor-auth-indicator-${membership.id}`}
													// disabled={!twoFactorAuthEnabled}
													position="bottom"
												>
													<div className="flex flex-column gap-2">
														<div>
															{membership.user.twoFactorAuth ? (
																<i className="mx-1 pi pi-check-circle text-green-400" />
															) : (
																<i className="mx-1 pi pi-times-circle text-red-400" />
															)}
															<span>
																{t(
																	'programPanel.memberships.userTwoFactorRefactorEnabledReason.byAccountSetting'
																)}
															</span>
														</div>
														<div>
															{Number(membership.user.stats?.organizationAdminCount) >
															0 ? (
																<i className="mx-1 pi pi-check-circle text-green-400" />
															) : (
																<i className="mx-1 pi pi-times-circle text-red-400" />
															)}
															<span>
																{t(
																	'programPanel.memberships.userTwoFactorRefactorEnabledReason.dueToOrganizationAdmin'
																)}
															</span>
														</div>
														<div>
															{Number(membership.user.stats?.programAdminCount) ? (
																<i className="mx-1 pi pi-check-circle text-green-400" />
															) : (
																<i className="mx-1 pi pi-times-circle text-red-400" />
															)}
															<span>
																{t(
																	'programPanel.memberships.userTwoFactorRefactorEnabledReason.dueToProgramAdmin'
																)}
															</span>
														</div>
														<div>
															{Number(membership.user.stats?.program2faReqCount) > 0 ? (
																<i className="mx-1 pi pi-check-circle text-green-400" />
															) : (
																<i className="mx-1 pi pi-times-circle text-red-400" />
															)}
															<span>
																{t(
																	'programPanel.memberships.userTwoFactorRefactorEnabledReason.dueToProgramSetting'
																)}
															</span>
														</div>
													</div>
												</Tooltip>
												<Box variant="white-bordered">
													<BoxSection>
														<h2>{membership.stats.userDisplayName}</h2>
														<p>Email: {membership.stats.userEmail}</p>
														<p>
															{t('programPanel.role')}:{' '}
															<span
																className={classNames('mx-2', {
																	'text-rose bold':
																		membership.role === ProgramRole.PROGRAM_ADMIN,
																	'text-purplish-blue':
																		membership.role === ProgramRole.USER,
																})}
															>
																{membership.role}
															</span>{' '}
															| {t('programPanel.usersLanguage')}:{' '}
															<span className="mx-2 text-purplish-blue">
																{membership.user.language || '-'}
															</span>{' '}
															| {t('programPanel.terms')}:{' '}
															<span className="mx-2">
																{membership.user.terms ? (
																	<span>
																		<i className="mx-1 pi pi-check-circle text-green-400" />
																	</span>
																) : (
																	<span>
																		<i className="mx-1 pi pi-times-circle text-red-400" />
																	</span>
																)}
															</span>
															| {t('programPanel.lastLogin')}:{' '}
															<span className="mx-2">
																{membership.user.lastLoginDate ? (
																	<span className="text-purplish-blue">
																		{dateFormat(
																			membership.user.lastLoginDate,
																			'full'
																		)}
																	</span>
																) : (
																	'-'
																)}
															</span>
															| {t('programPanel.twoFactorAuthEnabled')}:{' '}
															<span
																id={`two-factor-auth-indicator-${membership.id}`}
																className="mx-2"
															>
																{twoFactorAuthEnabled ? (
																	<span>
																		<i className="mx-1 pi pi-check-circle text-green-400" />
																	</span>
																) : (
																	<span>
																		<i className="mx-1 pi pi-times-circle text-red-400" />
																	</span>
																)}
															</span>
														</p>
														<div>
															<div className="flex flex-column sm:flex-row gap-2">
																<div className="flex flex-column flex-grow-1 gap-2">
																	<div className="flex flex-column align-items-center sm:flex-row flex-wrap gap-1">
																		{membership.user.deletedAt ? (
																			<Button
																				label={t(
																					'programPanel.memberships.deleteUserCancelBtn',
																					{
																						datetime: dateFormat(
																							dayjs(
																								membership.user
																									.deletedAt
																							).toDate(),
																							'date'
																						),
																					}
																				)}
																				onClick={() => {
																					setSelectedMembershipId(
																						membership.id
																					);
																					setDeletePersonalDataDialogOpen(
																						true
																					);
																				}}
																				loading={
																					removePersonalDataLoading &&
																					selectedMembershipId ===
																						membership.id
																				}
																				variant="danger-text"
																			/>
																		) : (
																			<>
																				{membership.applications.length > 0 ? (
																					<>
																						{membership.applications.map(
																							(application) => {
																								return (
																									<Link
																										key={
																											application.id
																										}
																										to={`/${panelPath}/application/${application.id}`}
																									>{`${
																										application.applicationRole
																									} (${
																										application.approved
																									}) ${
																										application.finished
																											? 'finished'
																											: ''
																									}`}</Link>
																								);
																							}
																						)}
																					</>
																				) : (
																					<>
																						<Button
																							label={t(
																								'programPanel.memberships.addProgramApplication'
																							)}
																							onClick={() => {
																								setSelectedUser(
																									membership?.user
																								);
																								setSelectedMembershipId(
																									membership.id
																								);
																								setChooseRoleModalOpen(
																									true
																								);
																							}}
																						/>
																						<Button
																							label={t(
																								'programPanel.memberships.removeProgramApplication'
																							)}
																							onClick={() => {
																								setSelectedMembershipId(
																									membership.id
																								);
																								setDeleteMembershipDialogOpen(
																									true
																								);
																							}}
																							loading={
																								removeMembershipLoading &&
																								selectedMembershipId ===
																									membership.id
																							}
																							variant="danger"
																						/>
																					</>
																				)}
																				{membership.canDelete && (
																					<Button
																						label={t(
																							'programPanel.memberships.removePersonalData'
																						)}
																						onClick={() => {
																							setSelectedMembershipId(
																								membership.id
																							);
																							setDeletePersonalDataDialogOpen(
																								true
																							);
																						}}
																						loading={
																							removePersonalDataLoading &&
																							selectedMembershipId ===
																								membership.id
																						}
																						variant="danger"
																					/>
																				)}
																			</>
																		)}
																	</div>
																</div>
																<div className="flex flex-column sm:flex-row gap-2">
																	<Button
																		label={t(
																			'organizationPanel.memberships.editDetails'
																		)}
																		onClick={() => {
																			setSelectedUser(membership.user);
																			setEditUserDialogOpen(true);
																		}}
																		variant="priamry-light"
																	/>
																	<Button
																		label={t(
																			'programPanel.memberships.changeProgramRole'
																		)}
																		onClick={() => {
																			const pairs = (
																				membership.coordinatePairs || []
																			).map(({ pairId }) => pairId);
																			roleFormReset({
																				role: membership.role,
																				pairs,
																			});
																			setChangeRoleDialogOpen(true);
																			setSelectedMembershipId(membership.id);
																		}}
																		loading={
																			manageMembershipLoading &&
																			manageMembershipVariables?.membershipId ===
																				membership.id
																		}
																		variant="primary-outlined"
																	/>
																</div>
															</div>
														</div>
													</BoxSection>
												</Box>
											</li>
										</Fragment>
									);
								})}
							</ul>
						)}
						<Paginator
							first={
								programMemberships.pagination.currentPage * programMemberships.pagination.itemsPerPage -
								programMemberships.pagination.itemsPerPage +
								1
							}
							rows={programMemberships.pagination.itemsPerPage}
							totalRecords={programMemberships.pagination.all}
							onPageChange={handlePageChange}
						/>
					</div>
				)}
			</div>
		</>
	);
};

export default ProgramMembershipList;
